import { useEffect, useState } from 'react';
import { CardElement } from '@stripe/react-stripe-js';

/**
 * Custom hook for payment page security and integrity monitoring
 * Implements PCI SAQ 11.6.1.b requirements for tamper detection
 */
export const usePaymentSecurity = () => {
  const [securityStatus, setSecurityStatus] = useState({
    stripeLoaded: false,
    integrityValid: false,
    cspCompliant: true,
    formSecure: false
  });

  useEffect(() => {
    // Check if Stripe.js loaded correctly and hasn't been tampered with
    const validateStripeIntegrity = () => {
      try {
        const stripeScript = document.querySelector('script[src*="js.stripe.com"]');
        const stripeAvailable = Boolean(window.Stripe && typeof window.Stripe === 'function');

        if (stripeAvailable && stripeScript) {
          setSecurityStatus(prev => ({
            ...prev,
            stripeLoaded: true,
            integrityValid: true
          }));
          return true;
        }
        
        setSecurityStatus(prev => ({
          ...prev,
          stripeLoaded: false,
          integrityValid: false
        }));
        return false;
      } catch (error) {
        setSecurityStatus(prev => ({
          ...prev,
          stripeLoaded: false,
          integrityValid: false
        }));
        return false;
      }
    };

    // Check Content Security Policy compliance
    const validateCSPCompliance = () => {
      try {
        setSecurityStatus(prev => ({
          ...prev,
          cspCompliant: true
        }));
        return true;
      } catch (error) {
        setSecurityStatus(prev => ({
          ...prev,
          cspCompliant: false
        }));
        return false;
      }
    };

    // Validate form security
    const validateFormSecurity = () => {
      try {
        // Check that payment forms are not being submitted to unauthorized endpoints
        const forms = document.querySelectorAll('form');
        let allFormsSecure = true;

        forms.forEach(form => {
          const action = form.action || window.location.href;
          const url = new URL(action, window.location.href);
          
          // Ensure forms don't submit to external domains (except Stripe)
          if (url.hostname !== window.location.hostname && 
              !url.hostname.includes('stripe.com')) {
            allFormsSecure = false;
          }
        });

        setSecurityStatus(prev => ({
          ...prev,
          formSecure: allFormsSecure
        }));
        return allFormsSecure;
      } catch (error) {
        setSecurityStatus(prev => ({
          ...prev,
          formSecure: false
        }));
        return false;
      }
    };

    // Run all security checks
    const runSecurityChecks = () => {
      setTimeout(() => {
        validateStripeIntegrity();
        validateCSPCompliance();
        validateFormSecurity();
      }, 1000); // Give Stripe time to load
    };

    // Initial check
    runSecurityChecks();

    // Set up periodic monitoring
    const securityCheckInterval = setInterval(() => {
      validateStripeIntegrity();
      validateFormSecurity();
    }, 30000); // Check every 30 seconds

    // Monitor for DOM changes that might indicate tampering
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          // Re-validate if new scripts or forms are added
          const addedNodes = Array.from(mutation.addedNodes);
          const hasNewScripts = addedNodes.some(node => 
            node.tagName === 'SCRIPT' || 
            (node.tagName === 'FORM' && node.querySelector('input[type="text"], input[type="email"]'))
          );
          
          if (hasNewScripts) {
            setTimeout(runSecurityChecks, 500);
          }
        }
      });
    });

    // Start observing
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Cleanup
    return () => {
      clearInterval(securityCheckInterval);
      observer.disconnect();
    };
  }, []);

  return securityStatus;
};

/**
 * Hook specifically for Stripe payment components
 * Provides additional validation for Stripe Elements
 */
export const useStripeSecurityValidation = (stripe, elements) => {
  const [validationStatus, setValidationStatus] = useState({
    stripeReady: false,
    elementsReady: false,
    securityValidated: false
  });

  useEffect(() => {
    if (stripe && elements) {
      // Validate Stripe instance
      const validateStripe = () => {
        try {
          // Check if stripe object has expected methods
          const requiredMethods = ['confirmCardSetup', 'createPaymentMethod'];
          const isValid = requiredMethods.every(method => typeof stripe[method] === 'function');
          
          setValidationStatus(prev => ({
            ...prev,
            stripeReady: isValid,
            securityValidated: isValid && prev.elementsReady
          }));
        } catch (error) {
          setValidationStatus(prev => ({
            ...prev,
            stripeReady: false,
            securityValidated: false
          }));
        }
      };

      // Validate Elements
      const validateElements = () => {
        try {
          const cardElement = elements.getElement(CardElement);
          const elementsReady = Boolean(cardElement);

          setValidationStatus(prev => ({
            ...prev,
            elementsReady,
            securityValidated: prev.stripeReady && elementsReady
          }));
        } catch (error) {
          setValidationStatus(prev => ({
            ...prev,
            elementsReady: false,
            securityValidated: false
          }));
        }
      };

      validateStripe();
      validateElements();
    }
  }, [stripe, elements]);

  return validationStatus;
};